import React, { useState, useEffect } from "react";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import "./app.scss";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import AuthenticationPage from "./pages/auth";
import store from "./store";
import "react-toastify/dist/ReactToastify.css";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Import ThemeContext
import ThemeContext from "./context/ThemeContext";
import ProtectedRoute from "./Protected/Protected";

const NavigationLayout = React.lazy(() => import("./pages/navigation"));

function App() {
  // Get initial theme from localStorage or default to false (light mode)
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("themePreference");
    return savedTheme === "dark";
  });

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          background: {
            default: darkMode ? "#262626" : "#ffffff",
            paper: darkMode ? "#262626" : "#ffffff",
          },
          text: {
            primary: darkMode ? "#ffffff" : "#333333",
            secondary: darkMode ? "#bbbbbb" : "#666666",
          },
        },
      }),
    [darkMode],
  );

  const toggleTheme = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem("themePreference", newMode ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleTheme }}>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <div className="app">
            <Router>
              <Routes>
                <Route
                  exact
                  path="/"
                  element={<Navigate replace to="/login" />}
                />
                <Route path="/login" element={<AuthenticationPage />} />

                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <NavigationLayout />
                      </React.Suspense>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Router>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme={darkMode ? "dark" : "light"}
            />
          </div>
        </ThemeProvider>
      </Provider>
    </ThemeContext.Provider>
  );
}

export default App;
